import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import config from 'constants/config'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import useCharterBundlePricing from 'hooks/CharterFlights/useCharterBundlePricing'
import type { CharterBundlePricingInput } from 'hooks/CharterFlights/useCharterBundlePricing'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { computeRewardBundlePrice, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'

interface Props {
  pricingToDisplay: App.PricingWithValue
  memberPricingToDisplay?: App.PricingWithValue
  totalGuests: number
  isPricePerPerson: boolean
  pkg: App.HotelPackage
  offer: App.Offer
  shouldShowValue: boolean
  showDiscountPill: boolean
  guestsDisplayText?: string
  shouldShowRoomType: boolean
  isUsingFallbackPricing: boolean
  pointsEarnCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  fareClass?: FareClassWithReward
  checkInDate?: string
  /** Multi-room aggregate hotel totals. When set the reward bundle uses these instead
   *  of `pricingToDisplay.price - totalFlightPrice` so the summary reflects the full
   *  multi-room hotel cost (matches the multi-room footer). */
  multiRoomPackageTotals?: { hotelPrice: number; hotelMemberPrice: number }
}

function mapOfferPricingToInput(
  pricingToDisplay: App.PricingWithValue,
  memberPricingToDisplay: App.PricingWithValue | undefined,
  showDiscountPill: boolean,
  totalGuests: number,
): CharterBundlePricingInput {
  return {
    price: pricingToDisplay.price,
    memberPrice: memberPricingToDisplay?.price,
    value: pricingToDisplay.value,
    discountPercent: showDiscountPill ? pricingToDisplay.discountPercent : undefined,
    totalGuests,
  }
}

function FlashOfferCharterSummary({
  pricingToDisplay,
  memberPricingToDisplay,
  totalGuests,
  isPricePerPerson,
  pkg,
  offer,
  shouldShowValue,
  showDiscountPill,
  guestsDisplayText,
  shouldShowRoomType,
  isUsingFallbackPricing,
  pointsEarnCalculationRequests,
  onViewPackageOptions,
  calendarsLoading,
  fareClass,
  checkInDate,
  multiRoomPackageTotals,
}: Props) {
  const intl = useIntl()

  const input = useMemo(
    () => mapOfferPricingToInput(pricingToDisplay, memberPricingToDisplay, showDiscountPill, totalGuests),
    [pricingToDisplay, memberPricingToDisplay, showDiscountPill, totalGuests],
  )
  const {
    displayPrice,
    memberDisplayPrice,
    valuePrice,
    discountPercentage,
    showMemberPricing,
  } = useCharterBundlePricing(input, isPricePerPerson)

  const {
    totalFlightPrice,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
  } = useOfferPageFlightPrice()

  const isRewardSelected = !!fareClass && isRewardFareClass(fareClass)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardLockedForLogin = isRewardSelected && !memberId
  const rewardNeedsDates = isRewardSelected && !checkInDate
  const needsRewardPreview = isRewardSelected && (rewardLockedForLogin || !hasLoyaltyAccount || rewardNeedsDates)

  // Preview pricing for logged-out/non-member/no-dates: hotel from package + hardcoded co-pay
  const rewardPreview = useMemo(() => {
    if (!needsRewardPreview || !fareClass) return undefined
    const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClass)]
    if (!rate) return undefined
    // pricingToDisplay includes flight — subtract to get hotel-only
    const hotelPrice = pricingToDisplay.price - totalFlightPrice
    const hotelMemberPrice = memberPricingToDisplay?.price ? memberPricingToDisplay.price - totalFlightPrice : undefined
    const pts = rate.pointsPerPerson * totalGuests
    const cash = hotelPrice + rate.cashPerPerson * totalGuests
    const memberCash = hotelMemberPrice ? hotelMemberPrice + rate.cashPerPerson * totalGuests : undefined
    return { points: pts, cash, memberCash }
  }, [needsRewardPreview, fareClass, pricingToDisplay.price, memberPricingToDisplay?.price, totalFlightPrice, totalGuests])

  const viewOptionsLabel = intl.formatMessage(
    {
      id: 'JaczHbGlU3Scp_uyLG7u',
      defaultMessage: 'View {travelSynonym} options',
      description: 'Action button label to view travel options on the offer detail page',
    },
    { travelSynonym: config.brandConfig.travelSynonym },
  )

  const nights = pkg.duration < MALDIVES_BUNDLE_DEFAULT_NIGHTS ? MALDIVES_BUNDLE_DEFAULT_NIGHTS : pkg.duration

  const nightsAndFlightsLabel = isUsingFallbackPricing
    ? intl.formatMessage(
        {
          id: 'MvPReD3cazr5vAzqCSxM',
          defaultMessage: '{nights} nights & flights from',
          description: 'Text showing nights and flights with from prefix for fallback pricing',
        },
        { nights },
      )
    : intl.formatMessage(
        {
          id: '4GssUqxdJT-Ju8SQ3FFn',
          defaultMessage: '{nights} nights & flights',
          description: 'Text showing nights and flights included in charter package',
        },
        { nights },
      )

  return (
    <div>
      <VerticalSpacer gap={16}>
        <Group direction="vertical" horizontalAlign="start" gap={8}>
          <Group direction="vertical" horizontalAlign="start">
            <PriceRowPriceCaption startIcon={<LinePlaneDepartureIcon />}>
              {nightsAndFlightsLabel}
            </PriceRowPriceCaption>
            {needsRewardPreview && rewardPreview && (() => {
              const isMember = rewardPreview.memberCash !== undefined
              const divisor = isPricePerPerson && totalGuests > 1 ? totalGuests : 1
              const cashDisplay = (rewardPreview.memberCash ?? rewardPreview.cash) / divisor
              const ptsDisplay = rewardPreview.points / divisor
              return (
                <VerticalSpacer gap={4}>
                  <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap" data-testid="pricing-reward-preview">
                    <Heading variant="heading4" colour={isMember ? 'lux-plus' : undefined}>
                      <FormattedMessage
                        id="8bmqjlgV1xPZXQCIpXGs"
                        defaultMessage="{points} + {cash}"
                        description="Reward-seat bundle price on the Flash hotel charter summary, showing the loyalty points portion and the cash co-pay portion combined as one heading."
                        values={{
                          points: <FormatLoyaltyPoints points={ptsDisplay} abbreviated />,
                          cash: <FormatCurrency value={cashDisplay} format="roundedDollar" />,
                        }}
                      />
                    </Heading>
                    <Caption variant="medium" colour={isMember ? 'lux-plus' : 'neutral-three'} weight="normal">
                      {isPricePerPerson
                        ? intl.formatMessage({ id: '7DhSxc9c0RQ3fZ0U48Py', defaultMessage: 'person', description: 'Per-person pricing unit suffix for reward price in the charter summary section' })
                        : intl.formatMessage({ id: 'j--PuUYLTgWUxAxROh0Z', defaultMessage: 'total', description: 'Total pricing unit suffix in the Maldives calendar' })}
                    </Caption>
                  </Group>
                </VerticalSpacer>
              )
            })()}
            {!rewardLockedForLogin && !rewardNeedsDates && rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null && (() => {
              // For multi-room, use the aggregate hotel total so the summary's
              // reward bundle matches the multi-room footer total. Single-room falls
              // back to the per-package math.
              const hotelPriceForReward = multiRoomPackageTotals
                ? multiRoomPackageTotals.hotelPrice
                : pricingToDisplay.price - totalFlightPrice
              const hotelMemberPriceForReward = multiRoomPackageTotals
                ? (multiRoomPackageTotals.hotelMemberPrice > 0 ? multiRoomPackageTotals.hotelMemberPrice : undefined)
                : (memberPricingToDisplay?.price ? memberPricingToDisplay.price - totalFlightPrice : undefined)
              const rewardPrice = computeRewardBundlePrice({
                hotelPrice: hotelPriceForReward,
                hotelMemberPrice: hotelMemberPriceForReward,
                rewardPointsTotal: rewardSeatPointsTotal,
                rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
              })
              const isMemberReward = rewardPrice.memberCash !== undefined
              const divisor = isPricePerPerson && totalGuests > 1 ? totalGuests : 1
              const rewardCashTotal = (rewardPrice.memberCash ?? rewardPrice.cash) / divisor
              const rewardPointsDisplay = rewardSeatPointsTotal / divisor
              const nonMemberCashTotal = rewardPrice.cash / divisor
              const nonMemberPointsDisplay = rewardSeatPointsTotal / divisor
              let rewardUnitLabel: string
              if (isPricePerPerson) {
                rewardUnitLabel = intl.formatMessage({
                  id: '7DhSxc9c0RQ3fZ0U48Py',
                  defaultMessage: 'person',
                  description: 'Per-person pricing unit suffix for reward price in the charter summary section',
                })
              } else {
                rewardUnitLabel = intl.formatMessage({
                  id: 'j--PuUYLTgWUxAxROh0Z',
                  defaultMessage: 'total',
                  description: 'Total pricing unit suffix in the Maldives calendar',
                })
              }
              return (
                <VerticalSpacer gap={4}>
                  <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap" data-testid="pricing-reward">
                    <Heading variant="heading4" colour={isMemberReward ? 'lux-plus' : undefined}>
                      <FormattedMessage
                        id="8bmqjlgV1xPZXQCIpXGs"
                        defaultMessage="{points} + {cash}"
                        description="Reward-seat bundle price on the Flash hotel charter summary, showing the loyalty points portion and the cash co-pay portion combined as one heading."
                        values={{
                          points: <FormatLoyaltyPoints points={rewardPointsDisplay} abbreviated />,
                          cash: <FormatCurrency value={rewardCashTotal} format="roundedDollar" />,
                        }}
                      />
                    </Heading>
                    <Caption variant="medium" colour={isMemberReward ? 'lux-plus' : 'neutral-three'} weight="normal">
                      {rewardUnitLabel}
                    </Caption>
                  </Group>
                  {isMemberReward && (
                    <Caption variant="medium" colour="neutral-two">
                      <FormattedMessage
                        id="dDulXESd_Db1yZjxr1Y5"
                        defaultMessage="Non-member {price}"
                        description="Price caption label for non-member price display in offer page"
                        values={{
                          price: <b><FormattedMessage id="cLzY6WOBBZLNvKoiqOho" defaultMessage="{points} + {cash}" description="Points plus cash for the non-member reward price on the charter summary section" values={{ points: <FormatLoyaltyPoints points={nonMemberPointsDisplay} abbreviated />, cash: <FormatCurrency value={nonMemberCashTotal} format="roundedDollar" /> }} /></b>,
                        }}
                      />
                    </Caption>
                  )}
                </VerticalSpacer>
              )
            })()}
            {!rewardLockedForLogin && !rewardNeedsDates && !(rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null) && (
              <LuxPlusPriceStack
                size="L"
                price={displayPrice ?? 0}
                memberPrice={showMemberPricing ? memberDisplayPrice : undefined}
                saleUnit={isPricePerPerson ? 'person' : 'total'}
                testid="pricing"
                horizontalAlign="start"
                offerConfig={offer.luxPlus}
              />
            )}
            {shouldShowValue && valuePrice !== undefined && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="L"
              originalValue={valuePrice}
              discountPercentage={discountPercentage}
            />}
            {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
            {shouldShowRoomType && (
              <Caption variant="medium" colour="light-foreground-alt">
                {pkg.roomType?.name}
              </Caption>
            )}
          </Group>
          <LoyaltyPointsEarn
            horizontalAlign="center"
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />
        </Group>
        <TextButton
          kind="primary"
          data-testid="OfferAvailable"
          onClick={onViewPackageOptions}
          fit="flex"
        >
          {viewOptionsLabel}
        </TextButton>
      </VerticalSpacer>
      <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
    </div>
  )
}

export default FlashOfferCharterSummary
